import React, { useState, useEffect, useRef } from 'react'
import '@jumio/websdk' // Import the Jumio SDK
import { API } from '../../../utility/API' // Import your API utility
import Styles from './Document.module.css'
import { CircularProgress } from '@mui/material'
import { useDispatch } from 'react-redux'
import { _getUserInfo } from '../../../redux/actions'
import { useNavigate } from 'react-router-dom'

const Document = () => {
  const [jumioToken, setJumioToken] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const jumioRef = useRef(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  useEffect(() => {
    // Fetch the Jumio SDK Token from the backend
    const fetchJumioToken = async () => {
      try {
        setIsLoading(true)
        const response = await API.post('authenticate/resource/kyc/initialize')
        setJumioToken(response.data.token)
        setError(null)
      } catch (error) {
        console.error("Error fetching Jumio token:", error)
        setError("Unable to initialize KYC verification. Please try again later.")
      } finally {
        setIsLoading(false)
      }
    }

    fetchJumioToken()
  }, [])

  useEffect(() => {
    if (jumioToken && jumioRef.current) {
      jumioRef.current.setAttribute('token', jumioToken)

      // Event listener for successful verification
      const handleSuccess = async (event) => {
        console.log('Jumio verification successful:', event.detail)

        try {
          // Update user's document label to show KYC is completed
          await API.put('authenticate/resource/labels', {
            key: 'document',
            value: 'sdk_completed'
          })

          // Refresh user data
          await dispatch(_getUserInfo())

          // Store transaction ID in URL for reference
          const transactionId = event.detail.transactionReference || event.detail.transactionId
          if (transactionId) {
            window.history.replaceState(
              {},
              '',
              `${window.location.pathname}?transactionStatus=SUCCESS&accountId=${transactionId}`
            )
          }

          // Redirect to KYC page to show updated status
          setTimeout(() => {
            navigate('/kyc')
          }, 1000)

        } catch (error) {
          console.error("Error updating KYC status:", error)
        }
      }

      // Event listener for errors
      const handleError = async (event) => {
        console.error('Jumio verification failed:', event.detail)

        try {
          // Reset label back to sdk_not_started if user cancels or errors occur
          await API.put('authenticate/resource/labels', {
            key: 'document',
            value: 'sdk_not_started'
          })

          // Refresh user data
          await dispatch(_getUserInfo())
        } catch (error) {
          console.error("Error updating KYC status:", error)
        }
      }

      jumioRef.current.addEventListener('jumio-success', handleSuccess)
      jumioRef.current.addEventListener('jumio-error', handleError)

      return () => {
        jumioRef.current?.removeEventListener('jumio-success', handleSuccess)
        jumioRef.current?.removeEventListener('jumio-error', handleError)
      }
    }
  }, [jumioToken, dispatch, navigate])

  if (error) {
    return (
      <div className={Styles.container}>
        <h2 className={Styles.title}>KYC Verification</h2>
        <div className={Styles.errorContainer}>
          <p className={Styles.errorMessage}>{error}</p>
          <button
            className={Styles.retryButton}
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={Styles.container}>
      <h2 className={Styles.title}>KYC Verification</h2>
      {jumioToken ? (
        <jumio-sdk style={{ flexDirection: 1 }} class='my-jumio-sdk' ref={jumioRef} dc='eu'></jumio-sdk>
      ) : (
        <div className={Styles.loadingContainer}>
          <CircularProgress />
          <p className={Styles.loadingText}>Initializing KYC verification...</p>
        </div>
      )}
    </div>
  )
}

export default Document
